import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "@/lib/router-compat";
import { cn } from "@/lib/utils";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useFavorites, useToggleFavorite } from "@/hooks/useFavorites";

interface Props {
  frameId: string;
  countrySlug: string;
  lang: "fr" | "en";
  /** Variante flottante posée sur une image produit. */
  floating?: boolean;
  className?: string;
}

export default function FavoriteButton({
  frameId,
  countrySlug,
  lang,
  floating = false,
  className,
}: Props) {
  const fr = lang === "fr";
  const navigate = useNavigate();
  const { user } = useAuthSession();
  const { data: favorites } = useFavorites();
  const toggle = useToggleFavorite();

  const isFavorite = !!favorites?.some((f) => f.frame_id === frameId);

  function onClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info(
        fr ? "Connectez-vous pour sauvegarder cette monture." : "Sign in to save this frame.",
      );
      navigate(`/${countrySlug}/login`);
      return;
    }
    toggle.mutate(
      { frameId, isFavorite },
      {
        onSuccess: (saved) => {
          toast.success(
            saved
              ? fr ? "Ajoutée à mes montures" : "Saved to my frames"
              : fr ? "Retirée de mes montures" : "Removed from my frames",
          );
        },
        onError: () =>
          toast.error(fr ? "Action impossible. Réessayez." : "Action failed. Try again."),
      },
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={toggle.isPending}
      aria-pressed={isFavorite}
      aria-label={
        isFavorite
          ? fr ? "Retirer de mes montures" : "Remove from my frames"
          : fr ? "Sauvegarder cette monture" : "Save this frame"
      }
      className={cn(
        "inline-flex items-center justify-center rounded-full transition-colors",
        floating
          ? "absolute top-2 right-2 z-10 h-9 w-9 bg-background/85 backdrop-blur border border-border shadow-sm"
          : "h-9 w-9 border border-border",
        className,
      )}
    >
      <Heart
        className={cn(
          "h-4 w-4",
          isFavorite ? "fill-destructive text-destructive" : "text-muted-foreground",
        )}
      />
    </button>
  );
}
